import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { Building2, Lock } from "lucide-react";

import { CheckoutInvoiceFields } from "@/components/features/payments/checkout-invoice-fields";
import { Button } from "@/components/ui/button";
import { CheckoutSummaryPanel } from "@/components/features/payments/checkout-summary-panel";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import {
  getCertificateCheckout,
  payCertificateCheckoutWithMercadoPago,
  switchCheckoutToBankTransfer,
  type MercadoPagoBrickFormData,
} from "@/services/certificatePaymentsService";
import { applyVerifyResponse, exchangeSsoCode } from "@/services/authService";
import { navigateAfterCertificatePaid } from "@/utils/certificatePaidNavigation";
import { isUuidParam } from "@/utils/isUuidParam";
import {
  loadMercadoPagoSdk,
  type MercadoPagoBrickController,
} from "@/utils/loadMercadoPagoSdk";
import { serverMessage } from "@/lib/serverMessage";
import { sleep } from "@/utils/sleep";

/**
 * Mercado Pago Checkout Bricks (spec c086002b, D5).
 *
 * The consumer never leaves the site. Payment Brick renders Mercado Pago's own
 * iframes for the card fields — so the card number never touches our DOM and
 * PCI stays SAQ A — and hands back a single-use token, which the server turns
 * into an order priced from the checkout row.
 *
 * THE DEVICE FINGERPRINT COMES FREE with this product choice and is Mercado
 * Pago's strongest fraud signal (11 points of their integration-quality
 * score). There is no device-id code here on purpose; do not add any.
 */

/** The public key is publishable by design — it only tokenises. */
const MERCADOPAGO_PUBLIC_KEY = (import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY ?? "").trim();

const BRICK_CONTAINER_ID = "mercadopago-payment-brick";

/**
 * How long we wait on a payment Mercado Pago has not settled yet (3DS, a cash
 * voucher). Mirrors the dLocal return page's bounded poll: the webhook and the
 * server's own status sync are the real backstops, so waiting here is a
 * courtesy rather than a correctness requirement.
 */
const PENDING_POLL_MS = 2500;
const PENDING_POLL_ATTEMPTS = 24;

type Phase = "loading" | "ready" | "confirming" | "error";

/** What the consumer is paying for, as the server itemised it. */
type Summary = {
  amountMinor: number;
  currency: string;
  items: Array<{ title: string; amountMinor: number }>;
  /** Where "volver" goes: the RFQ they came from, or their requests list. */
  backHref: string;
  invoice: {
    includeRut: boolean;
    rutDigits: string | null;
    legalName: string | null;
    address: string | null;
  };
  invoiceSuggestion: { rutDigits: string; legalName: string; address: string } | null;
};

/** Thrown to reject the Brick's onSubmit so it re-enables its own form. */
const DECLINED = "payment_declined";

export function MercadoPagoPaymentPage(): JSX.Element {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [switchingToTransfer, setSwitchingToTransfer] = useState(false);
  /** The validated checkout id, lifted out of the effect so the render can use it. */
  const [checkoutId, setCheckoutId] = useState<string | null>(null);

  /**
   * Rendered inside a host that supplies its own chrome — today the mobile
   * app's web view, which draws a navigation bar with its own back control.
   *
   * A QUERY PARAM rather than a user-agent sniff: the embedder knows it is
   * embedding and can simply say so, where a UA test guesses and is wrong
   * every time someone ships a new web view. Read once from the URL the page
   * was opened with, like checkout_id beside it.
   */
  const isEmbedded =
    new URLSearchParams(window.location.search).get("embed") === "1";

  /**
   * A one-time handoff code, when the mobile app opened this page.
   *
   * The app is already signed in and this page is not — different origins,
   * different storage — and every checkout endpoint is behind AuthGuard. The
   * first version solved that by INJECTING the app's own tokens into this
   * origin's localStorage, which is two clients sharing one credential: the
   * refresh token ROTATES, the portal spent it, and the app was signed out
   * mid-payment.
   *
   * So the app mints a code (POST /auth/sso/exchange's sibling, /auth/sso/handoff)
   * and this page trades it for a session OF ITS OWN. Single-use, seconds-long
   * TTL, and the same redemption endpoint an SSO callback uses.
   *
   * REDEEMED BEFORE ANYTHING ELSE RUNS, because the checkout load below needs
   * the session; and the code is stripped from the URL immediately afterwards
   * so a back/forward or a shared link cannot replay it — it is spent by then
   * anyway, but a URL that still carries a credential is a bad habit.
   */
  const [handoffDone, setHandoffDone] = useState(
    () => new URLSearchParams(window.location.search).get("handoff") == null,
  );
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("handoff");
    if (code == null) return;
    let cancelled = false;
    void (async () => {
      try {
        applyVerifyResponse(await exchangeSsoCode(code));
      } catch {
        // Falls through to whatever session this origin already had. If there
        // is none the checkout load fails next and says so — better than a
        // blank page that never explains itself.
      }
      params.delete("handoff");
      const query = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (query === "" ? "" : `?${query}`),
      );
      if (!cancelled) setHandoffDone(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * This route renders outside the app shell, which is what normally
   * supplies a scrolling pane — so without this the page cannot be scrolled
   * at all on a viewport shorter than its content, in a web view or in a
   * phone browser. Removed on unmount so the shell's own locking returns.
   */
  useEffect(() => {
    document.documentElement.classList.add("page-scroll");
    return () => document.documentElement.classList.remove("page-scroll");
  }, []);
  const brickRef = useRef<MercadoPagoBrickController | null>(null);

  useEffect(() => {
    /*
      WAITS FOR THE HANDOFF. Everything below is authenticated, so running it
      before a code has been redeemed would 401 on the first call and show
      "payment unavailable" to a consumer whose session was a few hundred
      milliseconds away. handoffDone starts TRUE when there is no code in the
      URL, so the ordinary web visit is unaffected.
    */
    if (!handoffDone) return;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("checkout_id")?.trim();
    // A hand-edited or truncated URL fails here rather than as a confusing
    // error from the server, and never reaches the payment endpoint.
    if (!isUuidParam(fromUrl)) {
      window.location.replace("/requests/new?certificate_payment=failed");
      return;
    }
    const id = fromUrl as string;
    setCheckoutId(id);

    /**
     * NOT a mount-once ref. React StrictMode runs this effect, tears it down,
     * and runs it again — and a guard that only remembers "already started"
     * leaves the second run returning early while the first run's teardown has
     * already cancelled the work, so the page sits on the spinner with no card
     * form at all. Instead each run owns its own flag, and whichever Brick
     * arrives after its own run was cancelled unmounts itself immediately.
     */
    let cancelled = false;

    /**
     * Waits for a payment Mercado Pago accepted but has not settled.
     *
     * Bounded, and a timeout is NOT a failure: the webhook or the status-sync
     * backstop will finish the checkout, so the consumer is sent to their
     * requests rather than told the payment did not work.
     */
    const waitForPending = async (): Promise<void> => {
      for (let i = 0; i < PENDING_POLL_ATTEMPTS; i++) {
        if (cancelled) return;
        await sleep(PENDING_POLL_MS);
        if (cancelled) return;
        try {
          const row = await getCertificateCheckout(id);
          if (row.status === "paid") {
            await navigateAfterCertificatePaid(id, row);
            return;
          }
          if (row.status === "failed" || row.status === "cancelled") {
            window.location.replace("/requests/new?certificate_payment=failed");
            return;
          }
        } catch (err) {
          // A 429 is the one worth backing off for; anything else just waits
          // for the next tick rather than abandoning the remaining attempts.
          if (axios.isAxiosError(err) && err.response?.status === 429) {
            await sleep(PENDING_POLL_MS);
          }
        }
      }
      // Re-checked: the consumer may already have navigated away, and yanking
      // them back a minute later would be worse than saying nothing.
      if (cancelled) return;
      window.location.replace("/requests");
    };

    /**
     * The payment is TAKEN AND CHARGED by the time this runs. Everything after
     * that is navigation, so a failure here must never re-enable the card form
     * — doing so would invite a second charge for a payment that already
     * succeeded.
     */
    const finishPaidPayment = async (): Promise<void> => {
      setPhase("confirming");
      try {
        const row = await getCertificateCheckout(id);
        await navigateAfterCertificatePaid(id, row);
      } catch {
        // Paid, but we could not read the row back to decide where to go. The
        // requests list shows the confirmed payment honestly, and the resume
        // banner picks up a request that was never created.
        window.location.replace("/requests");
      }
    };

    const onSubmit = async ({ formData }: { formData: MercadoPagoBrickFormData }): Promise<void> => {
      /**
       * SEND THE INNER formData. The callback receives
       * `{ selectedPaymentMethod, formData }` and forwarding the outer object
       * makes Mercado Pago answer `required_properties` — measured, and it
       * reads like a bug on our side.
       */
      let status: "paid" | "pending" | "failed";
      try {
        const res = await payCertificateCheckoutWithMercadoPago(id, formData);
        status = res.status;
      } catch (err) {
        /**
         * A FAILED REQUEST DOES NOT MEAN NOTHING WAS CHARGED. A timeout, a
         * dropped connection or a 502 can all arrive AFTER the server created
         * the order, so re-enabling the card form on the strength of the
         * error alone invites a second charge for a payment that succeeded.
         *
         * So we ask. The server is the only party that knows, and one GET is
         * cheap next to charging a consumer twice.
         */
        try {
          const row = await getCertificateCheckout(id);
          if (row.status === "paid") {
            await finishPaidPayment();
            return;
          }
        } catch {
          // Could not reach the server either. Fall through and let them
          // retry: the checkout is still pending, and the server's own
          // already-has-an-order guard catches a duplicate if one exists.
        }
        setPhase("ready");
        toast.error(serverMessage(err) ?? t("pay.mercadoPagoSubmitFailed"), { duration: 7000 });
        throw err;
      }

      if (status === "paid") {
        await finishPaidPayment();
        return;
      }
      if (status === "pending") {
        setPhase("confirming");
        await waitForPending();
        return;
      }
      /**
       * DECLINED, AND RETRYABLE. The checkout stays pending server-side
       * precisely so another card can be tried, so the Brick is handed back to
       * the consumer rather than the page being abandoned. Rejecting is what
       * re-enables its form.
       */
      setPhase("ready");
      toast.error(t("pay.mercadoPagoDeclined"), { duration: 7000 });
      throw new Error(DECLINED);
    };

    void (async () => {
      let amountMinor = 0;
      try {
        const row = await getCertificateCheckout(id);
        if (cancelled) return;
        // Already settled — most likely a refresh after paying, or the
        // dev/zero-amount bypass. Never render a card form over a paid row.
        if (row.status === "paid") {
          await navigateAfterCertificatePaid(id, row);
          return;
        }
        if (row.status !== "pending") {
          window.location.replace("/requests/new?certificate_payment=failed");
          return;
        }
        if (row.paymentMethod !== "mercadopago") {
          // Someone arrived here for a checkout that is not ours to take. The
          // other providers each have their own page.
          window.location.replace("/requests");
          return;
        }
        amountMinor = row.amountMinor;
        // THE AMOUNT GOES ON SCREEN BEFORE THE CARD FORM DOES. The first
        // version of this page asked for a card without ever showing what was
        // being charged -- the one thing a payer needs to see.
        setSummary({
          amountMinor: row.amountMinor,
          currency: row.currency,
          items: row.items ?? [],
          backHref:
            row.pricingSource === "quote" && row.quoteRequestId
              ? `/requests/${row.quoteRequestId}?source=quote`
              : "/requests",
          invoice: row.invoice ?? {
            includeRut: false,
            rutDigits: null,
            legalName: null,
            address: null,
          },
          invoiceSuggestion: row.invoiceSuggestion ?? null,
        });
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(serverMessage(err) ?? t("pay.mercadoPagoLoadFailed"));
        setPhase("error");
        return;
      }

      if (!MERCADOPAGO_PUBLIC_KEY) {
        // A deployment problem, not a consumer one — say so plainly rather
        // than rendering an empty box where the card form should be.
        setErrorMessage(t("pay.mercadoPagoUnavailable"));
        setPhase("error");
        return;
      }

      let MercadoPago;
      try {
        MercadoPago = await loadMercadoPagoSdk();
      } catch {
        if (cancelled) return;
        setErrorMessage(t("pay.mercadoPagoLoadFailed"));
        setPhase("error");
        return;
      }
      if (cancelled) return;

      const mp = new MercadoPago(MERCADOPAGO_PUBLIC_KEY, { locale: "es-UY" });

      try {
        const controller = await mp.bricks().create("payment", BRICK_CONTAINER_ID, {
          initialization: {
            /**
             * MAJOR units, and for DISPLAY ONLY. The Brick shows this and
             * submits it back beside the token; the server ignores what comes
             * back and charges the checkout row. Our rows are minor units.
             */
            amount: amountMinor / 100,
          },
          customization: {
            paymentMethods: {
              creditCard: "all",
              debitCard: "all",
              // Not declared: nothing is built to drive a ticket, a transfer
              // through Mercado Pago, or an account-money flow, and offering a
              // method we cannot complete strands the consumer mid-payment.
            },
            /**
             * THE BRICK WEARS OUR BRAND, not Mercado Pago's.
             *
             * Its default is a saturated blue button and 4px corners, which
             * next to our navy buttons and 8px radius reads as a third-party
             * widget dropped onto the page — and a payment form that looks
             * borrowed is the one thing a payment form cannot look.
             *
             * These values are the tokens from index.css, written literally
             * because the Brick is configured through a JS object the browser
             * hands to Mercado Pago's iframe: it cannot read a CSS variable
             * from our stylesheet. If the palette moves, this moves with it.
             */
            visual: {
              style: {
                customVariables: {
                  baseColor: "#0f2a44", // --color-primary
                  baseColorFirstVariant: "#1a3d5c", // --color-primary-hover
                  baseColorSecondVariant: "#1f3f66", // --color-secondary
                  textPrimaryColor: "#0f2a44", // --color-foreground
                  textSecondaryColor: "#6b7280", // --color-muted-foreground
                  inputBackgroundColor: "#ffffff",
                  formBackgroundColor: "#ffffff",
                  borderRadiusSmall: "6px", // --radius-sm
                  borderRadiusMedium: "8px", // --radius-DEFAULT
                  borderRadiusLarge: "10px", // --radius-lg
                  /**
                   * COLOUR AND RADIUS ONLY. Overriding the Brick's spacing and
                   * type sizes was a mistake: `formPadding: 0` collapsed its
                   * internal gutters and the method radios rendered ON TOP of
                   * their own card icons. Its layout is tuned to its own
                   * markup, which we cannot see and do not control — so we
                   * restyle what is safely ours (the palette, the corners) and
                   * leave the geometry to it.
                   */
                },
              },
              // Its own heading would be the SECOND one on this page. Ours
              // says what is happening; this one only labelled the widget.
              hidePaymentButton: false,
            },
          },
          callbacks: {
            onReady: () => {
              setPhase("ready");
            },
            onError: (err: unknown) => {
              // The Brick's own validation and tokenisation errors. It shows
              // its own message in the form; this is for us.
              console.error("Mercado Pago Brick error", err);
            },
            onSubmit,
          },
        });
        if (cancelled) {
          // This run was torn down while the Brick was being built. Unmount
          // the one we just created instead of leaking its iframes, and leave
          // the surviving run's Brick alone.
          controller.unmount();
          return;
        }
        brickRef.current = controller;
      } catch {
        if (cancelled) return;
        setErrorMessage(t("pay.mercadoPagoLoadFailed"));
        setPhase("error");
      }
    })();

    return () => {
      cancelled = true;
      // The Brick holds iframes and listeners of its own; leaving it mounted
      // across a navigation leaks both.
      brickRef.current?.unmount();
      brickRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoffDone]);

  /**
   * The brand bar and the way out. BOTH were missing: the page opened on a
   * bare heading with no sign of whose site it was, and once a consumer was
   * here the only way back was the browser's own button — on the screen where
   * they are about to enter a card, which is exactly where an unexplained
   * dead end reads as a scam.
   */
  const summaryPanel = (
    <CheckoutSummaryPanel
      amountMinor={summary?.amountMinor ?? null}
      currency={summary?.currency ?? "UYU"}
      items={summary?.items ?? []}
      backHref={summary?.backHref ?? "/requests"}
      hideChrome={isEmbedded}
    />
  );

  if (phase === "error") {
    return (
      <div className="lg:grid lg:min-h-svh lg:grid-cols-2">
        {summaryPanel}
        <div className="flex flex-col items-center justify-center gap-2 px-6 py-20 text-center">
          <p className="text-base font-semibold text-foreground">
            {t("pay.mercadoPagoErrorTitle")}
          </p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {errorMessage ?? t("pay.mercadoPagoLoadFailed")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:grid lg:min-h-svh lg:grid-cols-2">
      {summaryPanel}

      {/* The form half. NO CARD AROUND THE BRICK: it draws its own bordered
          method rows and brings its own padding, so a wrapper only stacked two
          insets and boxed a form that was already boxed. */}
      {/*
        TIGHTER WHEN EMBEDDED. The web page breathes because it is a full
        page; inside a web view it is one pane of a modal that already has
        its own header and insets, so the same padding reads as a gap the
        consumer has to scroll past to reach the form.
      */}
      <div
        className={
          isEmbedded ? "px-5 py-5" : "px-6 py-8 lg:px-10 lg:py-14"
        }
      >
        <div className="w-full lg:max-w-md">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            {t("pay.mercadoPagoTitle")}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {t("pay.mercadoPagoSubtitle")}
          </p>

          {(phase === "loading" || phase === "confirming") && (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <LoadingLogo size="lg" className="mx-auto" />
              <p className="text-sm text-muted-foreground">
                {phase === "confirming"
                  ? t("pay.mercadoPagoConfirming")
                  : t("pay.mercadoPagoLoading")}
              </p>
            </div>
          )}

          {/**
           * THE FACTURA QUESTION, asked at the till. It used to live in the
           * wizard, where a consumer deciding what to buy is not yet thinking
           * about who the invoice is for — and where the same person could not
           * buy once for their company and once for themselves.
           *
           * ABOVE THE BRICK, so it is answered BEFORE the pay button rather
           * than after it. The pay button belongs to Mercado Pago and renders
           * at the end of its own container, so anything below the Brick sits
           * past the point where the consumer has already decided to pay —
           * on a phone it was off-screen entirely. Asking first also leaves
           * the transfer alternative directly beneath the pay button, which
           * is where someone who cannot use a card is looking.
           */}
          {phase === "ready" && summary && checkoutId ? (
            /* Separated from the heading block above it. At mt-0 the box sat
               almost against the subtitle and read as part of that sentence
               rather than as the first thing to answer. */
            <div className={isEmbedded ? "mb-1 mt-3" : "mb-2 mt-5"}>
              <CheckoutInvoiceFields
                checkoutId={checkoutId}
                initial={summary.invoice}
                suggestion={summary.invoiceSuggestion}
              />
            </div>
          ) : null}

          {/**
           * ALWAYS IN THE DOM once we start mounting: the SDK writes into this
           * container by id, and a conditionally rendered node would not exist
           * when it goes looking. Hidden, not unmounted, while in flight.
           */}
          <div
            id={BRICK_CONTAINER_ID}
            /*
              Brick brings its own generous top spacing. On the web that
              sits under a roomy heading block and reads fine; embedded it
              stacks with ours into a gap the consumer scrolls past before
              reaching the first control.
            */
            className={
              phase === "loading" || phase === "confirming"
                ? "hidden"
                : isEmbedded
                  ? "mt-0"
                  : "mt-1"
            }
          />

          {phase === "ready" ? (
            <div className="mt-4 space-y-4">
              {/**
                * THE OTHER WAY TO PAY, directly under the card button where a
                * consumer who cannot use one is already looking. It was a text
                * link below a rule, which read as a footnote rather than as
                * the alternative it is — a card is not always possible (a
                * limit, a company account, someone who simply will not type
                * one) and an RFQ could not be paid any other way at all.
                *
                * IT NOW MATCHES THE TRANSFER PAGE'S OWN SWITCH (user,
                * 2026-10-12), divider and all: the two screens offer each
                * other as the alternative, and they were doing it in two
                * different visual languages.
                *
                * THIS REVERSES PART OF AN EARLIER CALL, which is worth knowing
                * before reversing it again. On 2026-10-09 the same user asked
                * for the pay button's geometry — full width, h-12, 8px — after
                * an outline-only version "looked disabled on a phone". The
                * shared outline Button keeps the full width and a large size
                * but drops the heavy border-2; if it reads as disabled on a
                * device again, that is the known failure mode, and the answer
                * is contrast rather than a return to border-2 primary.
                *
                * STILL SECONDARY IN COLOUR. Two filled buttons stacked would
                * make the consumer choose between things that look equally
                * primary, and this is the slower path — it waits on a human
                * and a receipt.
                */}
              <div className="my-5 flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">
                  {t("requests.user.bankTransfer.orPayAnotherWay")}
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>

              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full gap-2"
                disabled={switchingToTransfer}
                onClick={() => {
                  const checkoutId = new URLSearchParams(window.location.search)
                    .get("checkout_id")
                    ?.trim();
                  if (!checkoutId) return;
                  setSwitchingToTransfer(true);
                  void switchCheckoutToBankTransfer(checkoutId)
                    .then(() => {
                      window.location.assign(
                        `/pay/bank-transfer?checkout_id=${encodeURIComponent(checkoutId)}`,
                      );
                    })
                    .catch((err: unknown) => {
                      setSwitchingToTransfer(false);
                      toast.error(
                        serverMessage(err) ?? t("pay.mercadoPagoTransferFailed"),
                        { duration: 7000 },
                      );
                    });
                }}
              >
                <Building2 className="h-4 w-4" aria-hidden />
                {t("pay.mercadoPagoPreferTransfer")}
              </Button>

              <p className="flex items-start gap-2 text-xs text-muted-foreground">
                <Lock className="mt-0.5 size-3.5 shrink-0" />
                {t("pay.mercadoPagoSecureNote")}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
